'use client'

import { Console, Effect, pipe } from 'effect'
import { useCallback, useState } from 'react'
import { datadogLogs } from '@datadog/browser-logs'
import { getLobbyGames } from 'src/services/supabase/queries'
import type { MarbleGame } from 'src/types/domain/Game'
import { useGameActions, useGetPlayableIds, useAreAllGamesFetched, useAreAllowedGamesFetched, useIsGameAccessRestricted, useGetAllowedGameIds } from 'src/store/game'
import { useGetCurrentPlayerId } from 'src/store/player'

export function useGames() {
  const allGamesAlreadyFetched = useAreAllGamesFetched()
  const areAllowedGamesFetched = useAreAllowedGamesFetched()
  const isGameAccessRestricted = useIsGameAccessRestricted()
  const allowedGameIds = useGetAllowedGameIds()
  const [playableGamesFetched, setPlayableGamesFetched] = useState<boolean>(allGamesAlreadyFetched)
  const gameActions = useGameActions()
  const gameIds = useGetPlayableIds()
  const currentPlayerId = useGetCurrentPlayerId()

  const fetchPlayableGames = useCallback(async (currentGameId?: string) => {
    if (playableGamesFetched || !areAllowedGamesFetched) return
    const games = await pipe(getLobbyGames(), Effect.tapError(Console.error), Effect.runPromise)

    // Filter out games that are not in the allowedGameIds
    // No filter during transition period
    let allowedGames = [...games]
    if (isGameAccessRestricted) {
      allowedGames = games.filter((game) => allowedGameIds?.includes(game.id))
    }
    
    if (allowedGames.length === 0) {
      datadogLogs.logger.info('empty_allowed_games', {
        user_event: {
          currentGame: currentGameId,
          playerId: currentPlayerId,
        }
      })
    }

    const gamesRecord = allowedGames
      .reduce<Record<string, MarbleGame>>((acc, game) => { 
        acc[game.id] = game
        return acc
      }, {})
      
    gameActions.setPlayableGames(gamesRecord, currentGameId)
    setPlayableGamesFetched(true)
  }, [allowedGameIds, areAllowedGamesFetched, currentPlayerId, gameActions, isGameAccessRestricted, playableGamesFetched])

  return {
    hasGames: gameIds.length > 0,
    fetchPlayableGames,
    playableGamesFetched
  }
}
